import { useEffect, useRef } from 'react';
import type { ForexNewsEvent } from '../types/trading';
import { X, Brain, TrendingUp, TrendingDown, Minus, Clock, ExternalLink } from 'lucide-react';

const COUNTRY_FLAGS: Record<string, string> = {
  USD: '\u{1F1FA}\u{1F1F8}',
  EUR: '\u{1F1EA}\u{1F1FA}',
  GBP: '\u{1F1EC}\u{1F1E7}',
  JPY: '\u{1F1EF}\u{1F1F5}',
  AUD: '\u{1F1E6}\u{1F1FA}',
  NZD: '\u{1F1F3}\u{1F1FF}',
  CAD: '\u{1F1E8}\u{1F1E6}',
  CHF: '\u{1F1E8}\u{1F1ED}',
  CNY: '\u{1F1E8}\u{1F1F3}',
  ZAR: '\u{1F1FF}\u{1F1E6}',
};

interface Props {
  event: ForexNewsEvent | null;
  onClose: () => void;
}

export default function EventDetailOverlay({ event, onClose }: Props) {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!event) return null;

  const impactColor = (impact?: string) => {
    if (!impact) return 'text-gray-400';
    const l = impact.toLowerCase();
    if (l.includes('high')) return 'text-red-400';
    if (l.includes('medium')) return 'text-yellow-400';
    if (l.includes('low')) return 'text-green-400';
    return 'text-gray-400';
  };

  const impactBg = (impact?: string) => {
    if (!impact) return 'bg-gray-700/50';
    const l = impact.toLowerCase();
    if (l.includes('high')) return 'bg-red-500/15 border-red-500/30';
    if (l.includes('medium')) return 'bg-amber-500/15 border-amber-500/30';
    if (l.includes('low')) return 'bg-green-500/15 border-green-500/30';
    return 'bg-gray-700/50 border-gray-600/30';
  };

  const biasBg = (bias: string) => {
    if (bias === 'BUY') return 'bg-green-500/15 border-green-500/30';
    if (bias === 'SELL') return 'bg-red-500/15 border-red-500/30';
    return 'bg-gray-700/30 border-gray-600/30';
  };

  const biasColor = (bias: string) => {
    if (bias === 'BUY') return 'text-green-400';
    if (bias === 'SELL') return 'text-red-400';
    return 'text-gray-400';
  };

  const biasIcon = (bias: string) => {
    if (bias === 'BUY') return <TrendingUp className="w-4 h-4 text-green-400" />;
    if (bias === 'SELL') return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  const eventTime = new Date(event.date).toLocaleString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  const predictions = event.predictions ?? [];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="fixed right-0 top-0 bottom-0 z-50 w-[420px] bg-slate-800 border-l border-slate-700 flex flex-col animate-in slide-in-from-right duration-200"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-700">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">{COUNTRY_FLAGS[event.country] || ''}</span>
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{event.country}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${impactColor(event.impact)} ${impactBg(event.impact)}`}>
                {event.impact}
              </span>
            </div>
            <h2 className="font-bold text-gray-100 text-base leading-snug">{event.title}</h2>
            <div className="flex items-center gap-1.5 mt-1.5 text-xs text-slate-400">
              <Clock className="w-3.5 h-3.5" />
              <span>{eventTime}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-slate-700 transition-colors flex-shrink-0 mt-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Forecast', value: event.forecast, color: 'text-blue-300' },
              { label: 'Previous', value: event.previous, color: 'text-gray-300' },
              { label: 'Actual',   value: event.actual,   color: 'text-green-400' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-slate-700/50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500 mb-1">{label}</p>
                <p className={`font-mono font-semibold ${value ? color : 'text-slate-600'}`}>
                  {value || '—'}
                </p>
              </div>
            ))}
          </div>

          {/* AI Predictions */}
          <div>
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Brain className="w-3.5 h-3.5 text-purple-400" />
              AI Pair Predictions
            </h3>

            {predictions.length === 0 ? (
              <div className="bg-slate-700/30 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-500">No predictions generated yet for this event.</p>
                <p className="text-xs text-slate-600 mt-1">Predictions appear within the configured window before the event.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {predictions.map((pred, idx) => (
                  <div key={idx} className={`rounded-lg border p-3 ${biasBg(pred.bias)}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {biasIcon(pred.bias)}
                      <span className="font-bold text-gray-100">{pred.symbol}</span>
                      <span className={`font-bold ${biasColor(pred.bias)}`}>{pred.bias}</span>
                      <span className="text-slate-400 text-sm ml-auto">{pred.confidence}% confidence</span>
                    </div>
                    {pred.reasoning && (
                      <p className="text-xs text-slate-400 leading-relaxed">{pred.reasoning}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Source link */}
          {event.url && (
            <a
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View source
            </a>
          )}
        </div>
      </div>
    </>
  );
}
